import { Drawer, Text } from "@mantine/core"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { useMemo, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  CreateBusinessPartnerRequestDto,
  UpdateBusinessPartnerRequestDto,
  useFindTags,
} from "~/gen/api"
import { FormConfig, TypesForm } from "~/pages/Admin/components/TypesForm.tsx"
import { formSchema as contactFormSchema } from "~/pages/Brand/Suppliers/SuppliersRepository/Suppliers"
import { SupplierFormValues } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/columns"
import { useFields } from "~/pages/Brand/Suppliers/SuppliersRepository/utils/useFormFieldConfig"
import ProductionSitesInformation from "~/pages/SupplierPortal/ProductionSites/components/ProductionSitesInformation"

type SupplierMapping =
  | CreateBusinessPartnerRequestDto
  | UpdateBusinessPartnerRequestDto

type ContactDrawerProps = {
  opened: boolean
  setModalOpened: {
    readonly open: () => void
    readonly close: () => void
    readonly toggle: () => void
  }
  supplierMapping: SupplierMapping
  handleSupplierMapping: (values: SupplierMapping) => void
  isUpdate: boolean
}

export const ContactDrawer = ({
  opened,
  setModalOpened,
  supplierMapping,
  handleSupplierMapping,
  isUpdate = false,
}: ContactDrawerProps) => {
  const { t } = useTranslation(["organizationSettings"])
  const { data: tagsData } = useFindTags()

  const tagOptions = useMemo(() => {
    return (
      tagsData?.data?.tags.map((tag) => ({
        label: tag.name,
        value: tag.name,
      })) ?? []
    )
  }, [tagsData])

  const fields = useFields(isUpdate, tagOptions)

  const [reactFormConfig, setReactFormConfig] = useState<
    FormConfig<SupplierFormValues>
  >({
    validate: zod4Resolver(contactFormSchema),
    initialValues: supplierMapping,
  })

  useEffect(() => {
    setReactFormConfig({
      validate: zod4Resolver(contactFormSchema),
      initialValues: supplierMapping,
    })
  }, [supplierMapping])

  return (
    <Drawer
      position="right"
      opened={opened}
      onClose={setModalOpened.close}
      title={
        <Text component="span" fw={700} fz="h2">
          {t("supplierSettings.title")}
        </Text>
      }
      size="xl"
    >
      <TypesForm
        elements={fields}
        cancelLabel={t("supplierSettings.cancel")}
        submitLabel={t("supplierSettings.addSupplier")}
        formConfig={{
          ...reactFormConfig,
          initialValues: {
            ...reactFormConfig.initialValues,
            supplierId:
              "supplierId" in reactFormConfig.initialValues
                ? (reactFormConfig.initialValues.supplierId ?? "")
                : "",
          },
        }}
        mutate={handleSupplierMapping}
        closeDrawer={setModalOpened.close}
      >
        {"supplierId" in supplierMapping && supplierMapping.supplierId && (
          <ProductionSitesInformation supplierEmail={supplierMapping.email} />
        )}
      </TypesForm>
    </Drawer>
  )
}
